import React, { useState, useEffect, useRef } from 'react';
import {
  LuFileText,
  LuDownload,
  LuRefreshCw,
  LuSearch,
  LuFilter,
  LuX,
  LuAlertTriangle,
  LuInfo,
  LuAlertCircle,
  LuCheckCircle,
  LuClock,
  LuPlay,
  LuPause
} from 'react-icons/lu';

const Logs = ({ node, config, onConfigChange, loading, error, onSave, onDelete, saved }) => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const logsEndRef = useRef(null);

  // Sample log data
  const sampleLogs = [
    {
      id: 1,
      timestamp: new Date('2024-06-14T10:30:15'),
      level: 'info',
      message: 'Worker process started successfully',
      source: 'worker-core',
      details: 'PID: 12345, Memory: 45MB'
    },
    {
      id: 2,
      timestamp: new Date('2024-06-14T10:31:22'),
      level: 'debug',
      message: 'Processing batch of 100 items',
      source: 'data-processor',
      details: 'Batch ID: batch_001, Queue size: 450'
    },
    {
      id: 3,
      timestamp: new Date('2024-06-14T10:32:45'),
      level: 'warn',
      message: 'High memory usage detected',
      source: 'monitor',
      details: 'Current: 420MB, Threshold: 400MB'
    },
    {
      id: 4,
      timestamp: new Date('2024-06-14T10:33:12'),
      level: 'error',
      message: 'Failed to process item ID: item_5678',
      source: 'data-processor',
      details: 'Error: Validation failed - missing required field "email"'
    },
    {
      id: 5,
      timestamp: new Date('2024-06-14T10:34:30'),
      level: 'info',
      message: 'Batch processing completed successfully',
      source: 'data-processor',
      details: 'Processed: 99/100, Failed: 1, Duration: 3.2s'
    },
    {
      id: 6,
      timestamp: new Date('2024-06-14T10:35:15'),
      level: 'debug',
      message: 'Memory cleanup initiated',
      source: 'worker-core',
      details: 'Freed: 125MB, Remaining: 295MB'
    }
  ];

  useEffect(() => {
    setLogs(sampleLogs);
    setFilteredLogs(sampleLogs);
  }, []);

  useEffect(() => {
    let filtered = logs;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(log =>
        log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.source.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.details.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by level
    if (levelFilter !== 'all') {
      filtered = filtered.filter(log => log.level === levelFilter);
    }

    setFilteredLogs(filtered);
  }, [logs, searchTerm, levelFilter]);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        // Simulate new log entry
        const newLog = {
          id: Date.now(),
          timestamp: new Date(),
          level: ['info', 'debug', 'warn', 'error'][Math.floor(Math.random() * 4)],
          message: 'Auto-generated log entry',
          source: 'worker-core',
          details: `Timestamp: ${new Date().toISOString()}`
        };
        setLogs(prev => [...prev, newLog]);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [filteredLogs]);

  const getLevelIcon = (level) => {
    switch (level) {
      case 'error':
        return <LuAlertCircle className="w-4 h-4 text-red-400" />;
      case 'warn':
        return <LuAlertTriangle className="w-4 h-4 text-yellow-400" />;
      case 'info':
        return <LuInfo className="w-4 h-4 text-blue-400" />;
      case 'debug':
        return <LuCheckCircle className="w-4 h-4 text-green-400" />;
      default:
        return <LuInfo className="w-4 h-4 text-gray-400" />;
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'error':
        return 'text-red-400 bg-red-500/10 border-red-500/20';
      case 'warn':
        return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20';
      case 'info':
        return 'text-blue-400 bg-blue-500/10 border-blue-500/20';
      case 'debug':
        return 'text-green-400 bg-green-500/10 border-green-500/20';
      default:
        return 'text-gray-400 bg-gray-500/10 border-gray-500/20';
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).format(timestamp);
  };

  const handleRefresh = () => {
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  const handleDownload = () => {
    const logText = filteredLogs.map(log =>
      `[${log.timestamp.toISOString()}] ${log.level.toUpperCase()} ${log.source}: ${log.message}\n${log.details}\n`
    ).join('\n');

    const blob = new Blob([logText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${node?.data?.label || 'worker'}-logs-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white font-medium text-lg flex items-center gap-2">
              <LuFileText className="w-5 h-5" />
              Worker Logs
            </h3>
            <p className="text-white/60 text-sm mt-1">
              Monitor real-time execution logs and debug information
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`p-2 rounded transition-colors flex items-center gap-2 text-sm ${
                autoRefresh 
                  ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                  : 'bg-[#ffffff10] text-white/60 hover:bg-[#ffffff15]'
              }`}
              title={autoRefresh ? 'Disable auto-refresh' : 'Enable auto-refresh'}
            >
              {autoRefresh ? <LuPause className="w-4 h-4" /> : <LuPlay className="w-4 h-4" />}
              Auto-refresh
            </button>
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="p-2 bg-[#ffffff10] hover:bg-[#ffffff15] text-white/60 rounded transition-colors"
              title="Refresh logs"
            >
              <LuRefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleDownload}
              className="p-2 bg-[#ffffff10] hover:bg-[#ffffff15] text-white/60 rounded transition-colors"
              title="Download logs"
            >
              <LuDownload className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2">
              <LuSearch className="w-4 h-4 text-white/40" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search logs..."
              className="w-full pl-10 pr-10 py-2 bg-[#ffffff10] border border-[#ffffff20] rounded text-white text-sm placeholder-white/40 focus:outline-none focus:border-[#ffffff40]"
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
              >
                <LuX className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <LuFilter className="w-4 h-4 text-white/60" />
            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className="px-3 py-2 bg-[#ffffff10] border border-[#ffffff20] rounded text-white text-sm focus:outline-none focus:border-[#ffffff40]"
            >
              <option value="all">All Levels</option>
              <option value="error">Error</option>
              <option value="warn">Warning</option>
              <option value="info">Info</option>
              <option value="debug">Debug</option>
            </select>
          </div>
        </div>
      </div>

      {/* Logs Container */}
      <div className="flex-1 bg-[#ffffff05] border border-[#ffffff15] rounded-lg overflow-hidden flex flex-col">
        {/* Logs Header */}
        <div className="px-4 py-3 border-b border-[#ffffff15] bg-[#ffffff05]">
          <div className="flex items-center justify-between text-sm text-white/60">
            <span>Showing {filteredLogs.length} of {logs.length} entries</span>
            <div className="flex items-center gap-2">
              <LuClock className="w-4 h-4" />
              <span>Last updated: {formatTimestamp(new Date())}</span>
            </div>
          </div>
        </div>

        {/* Logs List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2 font-mono text-sm">
          {filteredLogs.length === 0 ? (
            <div className="text-center py-12">
              <LuFileText className="w-12 h-12 text-white/40 mx-auto mb-4" />
              <h4 className="text-white/80 font-medium mb-2">No logs found</h4>
              <p className="text-white/60 text-sm">
                {searchTerm || levelFilter !== 'all'
                  ? 'Try adjusting your search or filter criteria'
                  : 'No log entries available for this worker'}
              </p>
            </div>
          ) : (
            filteredLogs.map((log) => (
              <div
                key={log.id}
                className="p-3 bg-[#ffffff05] border border-[#ffffff10] rounded hover:bg-[#ffffff08] transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    {getLevelIcon(log.level)}
                    <span className="text-white/60 text-xs">
                      {formatTimestamp(log.timestamp)}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded border uppercase font-medium ${getLevelColor(log.level)}`}>
                      {log.level}
                    </span>
                    <span className="text-white/60 text-xs bg-[#ffffff10] px-2 py-1 rounded">
                      {log.source}
                    </span>
                  </div>
                </div>
                <div className="mt-2 ml-6">
                  <div className="text-white/90 mb-1">{log.message}</div>
                  {log.details && (
                    <div className="text-white/60 text-xs bg-[#ffffff05] px-2 py-1 rounded">
                      {log.details}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
          <div ref={logsEndRef} />
        </div>
      </div>

      {/* Stats Footer */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        {['error', 'warn', 'info', 'debug'].map((level) => {
          const count = logs.filter(log => log.level === level).length;
          return (
            <div key={level} className="p-3 bg-[#ffffff05] border border-[#ffffff15] rounded text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                {getLevelIcon(level)}
                <span className="text-white/80 text-sm capitalize">{level}</span>
              </div>
              <div className="text-white font-medium text-lg">{count}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Logs;